import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { finalize, from, mergeMap, take, tap } from 'rxjs';
import { MemberRosterService } from 'src/app/services/member.roster.service';
import { Member } from 'src/app/services/models/member';
import { ProviderDetailsExtend } from 'src/app/services/models/provider-detail';
import { ProviderMemberRoster } from 'src/app/services/models/provider-member-roster';
import { RosterItem } from 'src/app/services/models/roster-item';
import { ReportsService } from 'src/app/services/reports.service';
import { SessionService } from 'src/app/services/session.service';

@Component({
  selector: 'app-member-roster',
  templateUrl: './member-roster.component.html',
  styleUrl: './member-roster.component.scss'
})
export class MemberRosterComponent {

  @Input() member: Member;
  @Output() onSaveSuccess = new EventEmitter<string>();
  @Output() onSaveFailed = new EventEmitter<string>();
  @Output() onRosterChange = new EventEmitter();

  public providers: ProviderDetailsExtend[] = [];
  public displayproviders: ProviderDetailsExtend[] = [];
  public memberRosters: ProviderMemberRoster[] = [];
  public selectedrosterItem: RosterItem;
  public selectAll:boolean = false;
  public dataLoaded: boolean = false;
  private sortColumn: string;
  private sortAscending: boolean;
  public relationshipdate: Date;
  public readonly maxDate: Date = new Date();

  private reportService = inject(ReportsService);
  private memberRosterService = inject(MemberRosterService);
  private sessionService = inject(SessionService);

  private readonly nullRelationshipDate = "Relationship Start Date is a required field.";
  private readonly invalidRelationshipDate = "Relationship Start Date cannot be in the future.";
  private readonly noSelectedProviders = "No provider selected.";

// ****** data retrieval logic ******

// Logic to retrieve roster types
readonly getRosterItems$ = this.memberRosterService.getRosterItems()
.pipe(
  tap(rosterItems => {
    this.selectedrosterItem = rosterItems[0];
  },
));

/**
 * Logic to retreive member rosters
 * @param memberCin the specific member to assign to a roster
 * @param refresh   true to trigger a regresh of the UI, for example after saving member rosters
 * @returns Observable<ProviderMemberRoster[]>
 */
readonly getMemberRoster$ = (memberCin: string, refresh: boolean) =>
  this.memberRosterService.getMemberRoster([memberCin])
.pipe(
  tap(memberRosters => {
    this.memberRosters = memberRosters;
    if (refresh) {
      this.onChange(this.selectedrosterItem);
    }
  }));

/**
 * Logic to retrieve providers for rosters from a collection
 */
readonly getProvidersForPCPMemberRoster$ = this.reportService.getProvidersForMemberRoster(this.sessionService.currentPermission.providerCollectionID)
  .pipe(
    tap(providerDetail=>{
      providerDetail.forEach(x=>{
        let temp: ProviderDetailsExtend = new ProviderDetailsExtend();
        temp.copyFrom(x);
        this.providers.push(temp);
      });
      this.onChange(this.selectedrosterItem);
    })
  );

   /**
     * Logic to retrieve providers for a roster across all collections
     * @param searchPId Provier ID
     * @param searchPn  A provider's name, partial matches are ok
     * @param searchPT  Provider Tax ID (TIN)
     * @returns Observable<ProviderDetails[]> : providers for member rosters across all collections
     */
  readonly searchProvidersForPCPMemberRoster$ = (searchPId:string, searchPn:string, searchPT:string) =>
    this.reportService.searchProvidersForPCPMemberRoster(searchPId, searchPn, searchPT)
  .pipe(
    tap(providerDetails => {
      const providersEx: ProviderDetailsExtend[] = [];
      providerDetails.forEach(x=>{
        let temp: ProviderDetailsExtend = new ProviderDetailsExtend();
        temp.copyFrom(x);
        providersEx.push(temp);
      });

      this.providers = providersEx;
      this.onChange(this.selectedrosterItem);
    }));

  /**
   * Concurrently Loads initial data based on whether the user is internal or external
   */
  ngOnInit() {
    const dataLoader = this.getDataLoaders();
    from(dataLoader)
    .pipe(
     mergeMap((func) => func), // mergeMap calls functions concurrently and collects all results
     take(dataLoader.length), // automatic unsubscribe
     finalize(() => this.dataLoaded = true)
    )
    .subscribe();
  }

  /**
   *
   * @returns an array of promises that will load relevant data based on whether the user is internal or external
   */
  private getDataLoaders() {
    if(this.isInternalUser){
      return [this.getMemberRoster$(this.member.cin, false)];
    }
    else{
      return [this.getMemberRoster$(this.member.cin, false), this.getProvidersForPCPMemberRoster$];
    }
  }

  public onSelectAll(item: boolean) {
    this.selectAll = item;
    this.displayproviders.forEach(x=>{
      if(x.disabled)
        x.selected = x.disabled;
      else
        x.selected = this.selectAll;
    });
    this.onRosterChange.emit();
  }

  public onSelect(provider: ProviderDetailsExtend) {
    this.onRosterChange.emit();
  }

  public onChange(rosterItem: RosterItem) {
    let displayProviders : ProviderDetailsExtend[] = [];
    this.providers.forEach(displayProvider => {
      const displayProviderId = displayProvider.calProviderID.trim().toUpperCase();
      const foundRosterProvider = this.memberRosters
        .find(rosterProvider =>
          rosterProvider.calProviderId.trim().toUpperCase() === displayProviderId &&
          rosterProvider.rosterTypeID === rosterItem.rosterTypeID);

      displayProvider.selected = foundRosterProvider ? true : false;
      displayProvider.disabled = displayProvider.selected;

      displayProviders.push(displayProvider);
    });
    this.displayproviders = displayProviders;
  }

  public refreshRoster() {
    this.getMemberRoster$(this.member.cin, true).pipe(take(1)).subscribe();
  }

  public onSort(e: any): void {
    this.providers.sort((data1, data2) => {
      let value1 = data1[e.field];
      let value2 = data2[e.field];
      let result = null;

      if (value1 == null && value2 != null)
        result = -1;
      else if (value1 != null && value2 == null)
        result = 1;
      else if (value1 == null && value2 == null)
        result = 0;
      else if (typeof value1 === 'string' && typeof value2 === 'string')
        result = value1.localeCompare(value2);
      else
        result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;

      return (e.order * result);
    });
    this.sortAscending = e.order == 1;
    this.sortColumn = e.field;
  }

  public get isInternalUser() : boolean {
    return this.sessionService.isEnterpriseAdmin || this.sessionService.isEnterpriseUser;
  }

  public onSearch($event) {
    var searchPn = $event.providerName;
    var searchPId = $event.providerId;
    var searchPT = $event.providerTin

    if(this.isInternalUser) {
      this.searchProvidersForPCPMemberRoster$(searchPId, searchPn, searchPT).pipe(take(1)).subscribe();
    }
    else {
      let displayProviders: ProviderDetailsExtend[] = [];
      searchPId = searchPId?.trim().toUpperCase() ?? '';
      searchPT = searchPT?.trim().toUpperCase() ?? '';
      searchPn = searchPn?.trim().toUpperCase() ?? '';
      if(searchPId === '' && searchPT === '' && searchPn === ''){
        displayProviders = this.providers;
      }
      else{
        this.providers.forEach(x=>{
          if(searchPId !== '' && x.calProviderID.trim().toUpperCase() == searchPId) {
            displayProviders.push(x);
          }
          else if(searchPn !== '' && x.name.trim().toUpperCase().includes(searchPn)) {
            displayProviders.push(x);
          }
          else if(searchPT !== '' && x.taxId.trim().toUpperCase() == searchPT) {
            displayProviders.push(x);
          }
        });
      }
      this.displayproviders = displayProviders;
    }
  }

  public saveRoster() {
    try{
      let addedProviders: string[] = [];
      this.providers.forEach(x=>{
        if(x.selected && !x.disabled)
          addedProviders.push(x.calProviderID);
      });

      let validationErrorMessage: string = this.validateRosterRequest(addedProviders);
      let valid: boolean = validationErrorMessage ? false : true;

      if(valid) {
        this.memberRosterService.addMemberToProviderRoster(this.member.cin, this.selectedrosterItem.rosterTypeID, this.relationshipdate, addedProviders)
          .subscribe({next: (x)=>{
              this.refreshRoster();
              this.onSaveSuccess.emit("Roster updated successfully.");
            },
            error: (error)=>{
              this.onSaveFailed.emit("Error occurred during save. Please try again !!!");
            }
          });
      }
      else {
        this.onSaveFailed.emit(validationErrorMessage);
      }
    }
    catch(ex) {
      this.onSaveFailed.emit("Error occurred during save. Please try again !!!");
    }
  }

  private validateRosterRequest(addedProviders: string[]): string{
    let validationMsg:string = null;
    const today: Date = new Date();
    if(!this.relationshipdate){
      validationMsg = this.nullRelationshipDate;
    }
    else if(this.relationshipdate > today){
      validationMsg =  this.invalidRelationshipDate;
    }
    else if(addedProviders.length == 0) {
      validationMsg = this.noSelectedProviders;
    }
    return validationMsg;
  }

  public sortClass(column: string): string {
    let cls: string;
    if (column == this.sortColumn) {
      cls = this.sortAscending ? "fa fa-chevron-up" : "fa fa-chevron-down";
    }
    else {
      cls = "fa fa-chevron-up";
    }
    return cls;
  }

  public onSelectDate() {
  }
}
